<template>
  <div class="w-full relative">
    <div class="flex w-full relative" :data-row-id="rowData.id">
      <div class="w-full flex">
        <slot :editing="editing" :form-state="formState"></slot>
      </div>
      <div
        v-if="editing"
        class="flex items-center justify-end absolute right-0 top-0 bottom-0 z-[1] min-w-[80px] border_bottom px-2"
      >
        <a-button class="secondary_medium icon_button mr-2" @click="cancelEdit">
          <img class="size-4" :src="imagePath.neutral.grey.closeIconGrey" alt="cancel" />
        </a-button>
        <a-button class="primary_medium icon_button" @click="saveEdit">
          <ThemeImage :icon="imageNames.TICK_ICON" alt="save" class="size-4" />
        </a-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  reactive,
  provide,
  onMounted,
  watch,
  computed,
  getCurrentInstance,
} from "vue";
import imagePath from "@/src/paths/imagePath.js";
import { imageNames } from "@/src/constants/imageConstants.js";

const props = defineProps({
  rowData: {
    type: Object,
    required: true,
  },
  editConfig: {
    type: Object,
    default: () => ({
      editableHeaders: [],
      inputTypes: {},
      options: {},
    }),
  },
});

const emit = defineEmits(["save", "cancel", "edit"]);

const editing = ref(false);
const formState = reactive({});

const instance = getCurrentInstance();

const isNewRow = computed(() => props.rowData.isNewRow === true);

const startEdit = () => {
  initializeForm();
  editing.value = true;
  emit("edit", props.rowData);
};

const cancelEdit = () => {
  editing.value = false;
  emit("cancel", props.rowData);
};

const saveEdit = async () => {
  try {
    const updatedData = {
      ...props.rowData,
      ...formState,
    };

    const result = await emit("save", updatedData);

    if (result !== false) {
      editing.value = false;
    }
  } catch (error) {
    console.error("Error saving row:", error);
  }
};

const initializeForm = () => {
  Object.keys(formState).forEach((key) => {
    delete formState[key];
  });

  const { editableHeaders } = props.editConfig;

  if (editableHeaders && editableHeaders.length > 0) {
    for (const key of editableHeaders) {
      formState[key] =
        props.rowData[key] !== undefined ? props.rowData[key] : "";
    }
  } else {
    Object.keys(props.rowData).forEach((key) => {
      if (key !== "id") {
        formState[key] = props.rowData[key];
      }
    });
  }

  if (props.rowData.id) {
    formState.id = props.rowData.id;
  }

  if (props.rowData.isNewRow) {
    formState.isNewRow = true;
  }
};

provide("editableRowContext", {
  editing,
  formState,
  isNewRow,
  startEdit,
  saveEdit,
  cancelEdit,
  isInEditableRow: true,
});

onMounted(() => {
  initializeForm();

  if (props.rowData.isNewRow) {
    startEdit();
  }

  if (instance?.parent) {
    const parentTable = instance.parent.exposed;
    if (parentTable && typeof parentTable.registerEditableRow === "function") {
      parentTable.registerEditableRow(props.rowData.id, {
        startEdit,
        cancelEdit,
        saveEdit,
      });
    }
  }
});

watch(
  () => props.rowData,
  () => {
    if (editing.value) {
      initializeForm();
    }
  },
  { deep: true }
);

watch(
  () => props.rowData.isNewRow,
  (isNewRow) => {
    if (isNewRow) {
      startEdit();
    }
  }
);

defineExpose({
  startEdit,
  cancelEdit,
  saveEdit,
});
</script>

<style scoped>
.icon_button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  height: 32px;
  width: 32px;
}
</style>
